import React, { useEffect, useState, useCallback } from 'react';
import { Badge, Button, Modal, ModalHeader, ModalBody, ModalFooter, Form, FormGroup, Label, Input, Spinner, Progress, Alert, List } from 'reactstrap';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Grid, _ } from "gridjs-react";
import axios from 'axios';
import { verifyToken } from '../Common/AuthToken';
import { FaDownload, FaPlay, FaTrash, FaCloudUploadAlt } from "react-icons/fa";

const PlantStandCount = () => {
  const [uploadModal, setUploadModal] = useState(false);
  const [configModal, setConfigModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [datasetName, setDatasetName] = useState('');
  const [datasetDescription, setDatasetDescription] = useState('');
  const [uploadConsentGiven, setUploadConsentGiven] = useState(false);
  const [selectedSpecies, setSelectedSpecies] = useState('');
  const [selectedSeedingRate, setSeedingRate] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [useCustomModel, setUseCustomModel] = useState(false);
  const [customModel, setCustomModel] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [userReset, setUserReset] = useState(false);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [processComplete, setProcessComplete] = useState(false);
  const [results, setResults] = useState(null);
  const [consentGiven, setConsentGiven] = useState(false);
  const [userData, setUserData] = useState([]);
  const [userDatasets, setUserDatasets] = useState([]);
  const [pastAnalyses, setPastAnalyses] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentDatasetId, setCurrentDatasetId] = useState(null);
  
  const storedToken = localStorage.getItem('auth_token');

  const speciesOptions = [
    { value: 'corn', label: 'Corn' },
    { value: 'soybean', label: 'Soybean' },
    { value: 'cotton', label: 'Cotton' }
  ];

  const seedingRateOptions = {
    corn: ['30,000 seeds/acre', '32,000 seeds/acre', '34,000 seeds/acre', '36,000 seeds/acre'],
    soybean: ['120,000 seeds/acre', '140,000 seeds/acre', '160,000 seeds/acre'],
    cotton: ['40,000 seeds/acre', '50,000 seeds/acre', '60,000 seeds/acre']
  };

  const modelOptions = {
    corn: ['Corn Detection Model v1 (Default)'],
    soybean: ['Soybean Detection Model v1 (Default)'],
    cotton: ['Cotton Detection Model v1 (Default)']
  };

  const toggleUploadModal = () => {
    setUploadModal(!uploadModal);
    if (!uploadModal) {
      // Reset upload state when opening modal
      setUploadedFile(null);
      setUploadProgress(0);
      setDatasetName('');
      setDatasetDescription('');
      setUploadConsentGiven(false);
    }
  };

  const toggleConfigModal = () => {
    setConfigModal(!configModal);
    if (!configModal) {
      // Reset config state when opening modal
      setSelectedSpecies('');
      setSeedingRate('');
      setSelectedModel('');
      setUseCustomModel(false);
      setCustomModel(null);
      setConsentGiven(false);
    }
  };

  // Fetch user data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = { token: storedToken };
        const tokenResponse = await verifyToken(user);
        setUserData([tokenResponse?.data]);
      } catch (error) {
        console.error('Error verifying token:', error);
        toast.error('Error fetching user data. Please try again.');
      }
    };

    fetchData();
  }, [storedToken]);

  // Fetch user datasets — extracted as reusable callback so upload/delete can refresh without full page reload
  const fetchUserDatasets = useCallback(async () => {
    try {
      const response = await axios.get(`/api/getUploadedDatasets`, {
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.data.status === 1) {
        const datasets = response.data.data || [];
        const datasetsArray = datasets.map((dataset) => {
          const uploadDate = new Date(dataset.createdAt).toLocaleString('en-GB', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
          });

          const retentionDate = new Date(dataset.retentionUntil).toLocaleDateString('en-GB', {
            year: 'numeric',
            month: 'short',
            day: '2-digit'
          });

          const fileSize = (dataset.fileSize / (1024 * 1024 * 1024)).toFixed(2);
          const daysLeft = Math.ceil((new Date(dataset.retentionUntil) - new Date()) / (1000 * 60 * 60 * 24));

          return [
            dataset.datasetName,
            dataset.datasetDescription || '-',
            uploadDate,
            _(
              <Badge color={
                dataset.uploadStatus === "uploaded" ? "success" :
                dataset.uploadStatus === "uploading" ? "warning" :
                dataset.uploadStatus === "failed" ? "danger" : "secondary"
              }>
                {dataset.uploadStatus || "unknown"}
              </Badge>
            ),
            `${fileSize} GB`,
            dataset.fileFormat,
            _(
              <span>
                {retentionDate}
                <br />
                <small className={`text-${daysLeft <= 3 ? 'danger' : 'muted'}`}>
                  ({daysLeft} days left)
                </small>
              </span>
            ),
            _(
              <div className="d-flex gap-1">
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => handleStartProcess(dataset.datasetId)}
                  title="Start Analysis"
                >
                  <FaPlay />
                </Button>
                <Button
                  color="danger"
                  size="sm"
                  onClick={() => handleDeleteDataset(dataset.datasetId)}
                  title="Delete Dataset"
                >
                  <FaTrash />
                </Button>
              </div>
            )
          ];
        });

        setUserDatasets(datasetsArray);
      }
    } catch (error) {
      console.error('Error fetching datasets:', error);
    }
  }, [storedToken]);

  useEffect(() => {
    if (userData.length > 0) {
      fetchUserDatasets();
    }
  }, [userData, fetchUserDatasets]);

  // Fetch SLURM jobs from the new backend
  const fetchPlantStandCountJobs = useCallback(async () => {
    try {
      const response = await axios.get('/api/getPlantStandCountJobs', {
        headers: { 'Authorization': `Bearer ${storedToken}` }
      });
      if (response.data.status === 1) {
        const jobsArray = response.data.data.map((job) => [
          job.jobId,
          job.userEmail,
          job.datasetId,
          job.species || '-',
          new Date(job.createdAt).toLocaleString('en-GB', {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit', hour12: false
          }),
          _(
            <Badge color={
              job.jobStatus === 'completed' ? 'success' :
              job.jobStatus === 'running' ? 'primary' :
              job.jobStatus === 'queued' || job.jobStatus === 'submitted' ? 'warning' :
              job.jobStatus === 'failed' ? 'danger' : 'secondary'
            }>{job.jobStatus}</Badge>
          ),
          _(
            <div>
              {job.jobStatus === 'completed'
                ? <span className="text-success">✓ Ready</span>
                : job.jobStatus === 'running'
                ? <Spinner size="sm" color="primary" />
                : '-'}
            </div>
          ),
          _(
            <div>
              {job.jobStatus === 'completed' && (
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => handleDownload(job.jobId)}
                  title={`Download results for Job ${job.jobId.slice(0, 8)}...`}
                >
                  <FaDownload />
                </Button>
              )}
              {(job.jobStatus === 'running' || job.jobStatus === 'queued' || job.jobStatus === 'submitted') && (
                <Spinner size="sm" color="primary" />
              )}
            </div>
          )
        ]);
        setPastAnalyses(jobsArray);

        // Auto-advance step indicator — only if user hasn't manually reset
        if (response.data.data.length > 0 && !userReset) {
          const latestJob = response.data.data[0]; // already sorted by createdAt desc
          if (latestJob.jobStatus === 'completed') {
            setCurrentStep(4);
            setProcessComplete(true);
            setResults({
              jobId: latestJob.jobId,
              resultZipPath: latestJob.resultZipPath,
            });
          } else if (
            latestJob.jobStatus === 'running' ||
            latestJob.jobStatus === 'submitted' ||
            latestJob.jobStatus === 'queued'
          ) {
            setCurrentStep(3);
            setProcessing(true);
            const statusProgressMap = {
              submitted: 10,
              queued: 20,
              running: 60,
            };
            setProcessingProgress(statusProgressMap[latestJob.jobStatus] || 10);
            pollJobStatus(latestJob.jobId);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
    }
  }, [storedToken]);

  useEffect(() => {
    if (userData.length > 0) {
      fetchPlantStandCountJobs();
    }
  }, [userData, fetchPlantStandCountJobs]);

  // File upload handler
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const fileName = file.name.toLowerCase();
      if (!fileName.endsWith('.tiff') && !fileName.endsWith('.tif')) {
        toast.error('Please upload a TIFF file format');
        return;
      }
      
      // Validate file size (max 20GB)
      const maxSize = 20 * 1024 * 1024 * 1024; // 20GB in bytes
      if (file.size > maxSize) {
        toast.error('File size exceeds 20GB limit.');
        return;
      }

      setUploadedFile(file);
      toast.success('File selected successfully!');
    }
  };

  // ===== UPDATED 3-PHASE CEPH UPLOAD =====
  const handleUploadSubmit = async () => {
    if (!uploadedFile) {
      toast.error('Please select a file first');
      return;
    }

    if (!datasetName.trim()) {
      toast.error('Please enter a dataset name');
      return;
    }

    if (!uploadConsentGiven) {
      const userConfirmed = window.confirm(
      'Do you want to continue without providing consent?\n\n' +
      'Providing consent helps us improve our model detection accuracy by training on your data. ' +
      'Your data will be stored securely for 14 days.\n\n' +
      'Click OK to continue without consent, or Cancel to go back and provide consent.'
      );
  
      if (!userConfirmed) {
        return; // User cancelled, don't upload
      }
    }

    setIsLoading(true);
    setUploadProgress(0);

    try {
      console.log('Phase 1: Initializing upload...');
      const initResponse = await axios.post('/api/uploadDataset', {
        datasetName: datasetName.trim(),
        datasetDescription: datasetDescription.trim(),
        fileName: uploadedFile.name,
        fileSize: uploadedFile.size,
        fileType: uploadedFile.type || 'application/octet-stream',
        consentGiven: uploadConsentGiven

      }, {
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (initResponse.data.status !== 1) {
        throw new Error(initResponse.data.msg || 'Failed to initialize upload');
      }

      const { datasetId, uploadUrl } = initResponse.data;
      console.log('Phase 1 complete. DatasetId:', datasetId);

      // ===== PHASE 2: Upload File via Presigned POST =====
      console.log('Phase 2: Uploading file to Ceph via presigned POST...');

      const { url, fields } = initResponse.data.upload;

      const formData = new FormData();
      Object.entries(fields).forEach(([k, v]) => formData.append(k, v));
      formData.append("file", uploadedFile);

      await axios.post(url, formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(percentCompleted);
          console.log(`Upload progress: ${percentCompleted}%`);
        },
      });
      console.log('Phase 2 complete. File uploaded to Ceph.');


      // ===== PHASE 3: Mark Upload as Complete =====
      console.log('Phase 3: Marking upload as complete...');
      const completeResponse = await axios.post('/api/completeUpload', {
        datasetId: datasetId,
        bucket: initResponse.data.bucket,
        objectKey: initResponse.data.objectKey
      }, {
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (completeResponse.data.status !== 1) {
        throw new Error('Failed to finalize upload');
      }
      console.log('Phase 3 complete. Upload finalized.');

      // Success!
      toast.success(`Dataset "${datasetName}" uploaded successfully!`);
      setUploadModal(false);
      setIsLoading(false);
      setUploadProgress(0);
      
      // Refresh datasets list without full page reload
      fetchUserDatasets();

    } catch (error) {
      console.error('Error uploading dataset:', error);
      
      let errorMessage = 'Failed to upload dataset. Please try again.';
      
      if (error.response?.data?.msg) {
        errorMessage = error.response.data.msg;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  const handleConfigSubmit = async () => {
    setUserReset(false);
    if (!selectedSpecies || !selectedSeedingRate || (!selectedModel && !customModel)) {
      toast.error('Please fill in all required fields.');
      return;
    }
    if (!consentGiven) {
      toast.error('Please provide consent to use your data for model training purposes.');
      return;
    }
    setConfigModal(false);
    setProcessing(true);
    setCurrentStep(3);
    try {
      const response = await axios.post('/api/submitPlantStandCountJob', {
        datasetId: currentDatasetId,
        species: selectedSpecies,
        seedingRate: selectedSeedingRate,
      }, {
        headers: {
          'Authorization': `Bearer ${storedToken}`,
          'Content-Type': 'application/json',
        }
      });
      if (response.data.status === 1) {
        const { jobId } = response.data.data;
        toast.success(`Job submitted! ID: ${jobId}`);
        pollJobStatus(jobId);
      } else {
        throw new Error(response.data.msg || 'Failed to submit job');
      }
    } catch (error) {
      console.error('Error submitting job:', error);
      toast.error(error.response?.data?.msg || error.message || 'Failed to start processing.');
      setProcessing(false);
      setCurrentStep(2);
    }
  };

  const pollJobStatus = (jobId) => {
    const interval = setInterval(async () => {
      try {
        const response = await axios.get(`/api/getPlantStandCountJobStatus/${jobId}`, {
          headers: { 'Authorization': `Bearer ${storedToken}` }
        });
        if (response.data.status === 1) {
          const job = response.data.data;
          const statusProgressMap = {
            created: 5,
            submitted: 10,
            queued: 20,
            running: 60,
            completed: 100,
            failed: 0,
            cancelled: 0,
          };
          setProcessingProgress(statusProgressMap[job.jobStatus] || 0);
          if (job.jobStatus === 'completed') {
            clearInterval(interval);
            setProcessing(false);
            setProcessComplete(true);
            setCurrentStep(4);
            setResults({
              jobId: job.jobId,
              resultZipPath: job.resultZipPath,
            });
            toast.success('Processing complete! Results are ready.');
            fetchPlantStandCountJobs();
          } else if (job.jobStatus === 'failed' || job.jobStatus === 'cancelled') {
            clearInterval(interval);
            setProcessing(false);
            toast.error(`Job ${job.jobStatus}. Please try again.`);
            setCurrentStep(1);
          }
        }
      } catch (error) {
        console.error('Error polling job status:', error);
      }
    }, 10000); // Poll every 10 seconds (SLURM jobs take time)
  };

  const handleDownload = async (jobId) => {
    try {
      // Construct the known CEPH path directly from jobId
      const objectKey = `results/plantstandcount/${jobId}/result.zip`;

      const dlResponse = await axios.post('/api/getDownloadUrl', {
        objectKey: objectKey,
      }, {
        headers: { 'Authorization': `Bearer ${storedToken}` }
      });
      if (dlResponse.data.status === 1) {
        window.open(dlResponse.data.url, '_blank');
        toast.info('Downloading results...');
      } else {
        toast.error('Failed to get download URL');
      }
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download results');
    }
  };

  const handleStartProcess = (datasetId) => {
    setCurrentDatasetId(datasetId);
    setConfigModal(true);
    setCurrentStep(2);
  };

  const handleDeleteDataset = async (datasetId) => {
  if (!window.confirm("Are you sure you want to delete this dataset? This action cannot be undone.")) return;

  try {
    const resp = await axios.delete(`/api/deleteDataset/${datasetId}`, {
      headers: { Authorization: `Bearer ${storedToken}` },
    });

    if (resp.data.status === 1) {
      toast.success("Dataset deleted successfully");
      fetchUserDatasets();
    } else {
      toast.error(resp.data.msg || "Failed to delete dataset");
    }
  } catch (e) {
    toast.error(e.response?.data?.msg || "Failed to delete dataset");
  }
};

  const resetProcess = () => {
    setUserReset(true);
    setCurrentStep(1);
    setUploadedFile(null);
    setUploadProgress(0);
    setSelectedSpecies('');
    setSeedingRate('');
    setSelectedModel('');
    setProcessComplete(false);
    setProcessing(false);
    setProcessingProgress(0);
    setResults(null);
    setConsentGiven(false);
    setCurrentDatasetId(null);
    fetchPlantStandCountJobs();
  };

  return (
    <div>
      <ToastContainer autoClose={3000} limit={1} draggable pauseOnHover />

      <h5 className="fw-bold mb-3">Plant Stand Count</h5>
      
      {/* Action Button */}
      <div className="mb-4">
        <Button color="primary" onClick={toggleUploadModal}>
          Upload New Dataset
        </Button>
      </div>

      {/* Step Indicator */}
      <div className="mb-4 p-3 bg-light rounded">
        <div className="d-flex justify-content-between align-items-center">
          {[
            { num: 1, label: 'Upload Dataset' },
            { num: 2, label: 'Configure' },
            { num: 3, label: 'Processing' },
            { num: 4, label: 'Results' }
          ].map((step, idx) => (
            <div key={idx} className="d-flex flex-column align-items-center flex-grow-1 position-relative">
              <div 
                className={`rounded-circle d-flex align-items-center justify-content-center fw-bold ${
                  currentStep >= step.num ? 'bg-primary text-white' : 'bg-secondary text-white'
                }`}
                style={{ width: '40px', height: '40px', zIndex: 1 }}
              >
                {currentStep > step.num ? '✓' : step.num}
              </div>
              <small className={`mt-2 ${currentStep >= step.num ? 'text-primary fw-semibold' : 'text-muted'}`}>
                {step.label}
              </small>
            </div>
          ))}
        </div>
      </div>

      {/* Processing Status */}
      {processing && (
        <Alert color="info" className="d-flex align-items-center">
          <Spinner size="sm" className="me-3" />
          <div className="flex-grow-1">
            <strong>Processing Your Dataset...</strong>
            <Progress value={processingProgress} className="mt-2" animated />
            <small className="text-muted mt-1 d-block">{processingProgress}% complete</small>
          </div>
        </Alert>
      )}

      {/* Results Display */}
      {processComplete && results && (
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0">✅ Processing Complete</h5>
            <div>
              <Button color="success" className="me-2" onClick={() => handleDownload(results.jobId)}>
                <FaDownload className="me-2" />
                Download Latest Results (ZIP)
              </Button>
            </div>
          </div>
          <Alert color="success">
            Your results are ready. Click "Download Results" to get the ZIP file containing CSV data, visualizations, and metadata.
            <br /><small className="text-muted">Job ID: {results.jobId}</small>
          </Alert>
        </div>
      )}

      {/* User Datasets Table */}
      <h5 className="fw-bold mb-3">User Datasets</h5>
      <Grid
        columns={[
          "Dataset Name",
          "Description",
          "Uploaded Date",
          "Upload Status",
          "Size",
          "Type",
          "Retention Until",
          "Actions"
        ]}
        data={userDatasets}
        search= {true}
        language={{
          search: {
            placeholder: "Search datasets by name",
          },
        }}
        sort
        pagination={{
          enabled: true,
          limit: 10,
        }}
      />

      {/* Past Analyses */}
      <h5 className="fw-bold mb-3 mt-4">Past Analyses</h5>
      <Grid
        columns={[
          "Job ID",
          "User Email",
          "Dataset ID",
          "Species",
          "Submitted Date",
          "Status",
          "Results",
          "Actions"
        ]}
        data={pastAnalyses}
        search= {true}
        language={{
          search: {
            placeholder: "Search analyses by Process ID or dataset name",
          },
        }}
        sort
        pagination={{
          enabled: true,
          limit: 10,
        }}
      />

      {/* Upload Modal */}
      <Modal isOpen={uploadModal} toggle={toggleUploadModal} size="lg">
        <ModalHeader toggle={toggleUploadModal}>Upload Orthomosaic Dataset</ModalHeader>
        <ModalBody>
          {/* Dataset Name Input */}
          <FormGroup className="mb-3">
            <Label for="datasetName">Dataset Name *</Label>
            <Input
              type="text"
              id="datasetName"
              placeholder="Enter the Dataset name"
              value={datasetName}
              onChange={(e) => setDatasetName(e.target.value)}
              maxLength={100}
            />
            <small className="text-muted">Give this dataset a nickname</small>
          </FormGroup>

          {/* Dataset Description (Optional) */}
          <FormGroup className="mb-3">
            <Label for="datasetDescription">Dataset Description (Optional)</Label>
            <Input
              type="textarea"
              id="datasetDescription"
              placeholder="Enter the Dataset description"
              value={datasetDescription}
              onChange={(e) => setDatasetDescription(e.target.value)}
              maxLength={500}
              rows={3}
            />
            <small className="text-muted">Add notes about this dataset</small>
          </FormGroup>

          {/* File Upload Area */}
          <div 
            className="border border-primary border-2 rounded p-5 text-center mb-3"
            style={{ borderStyle: 'dashed', backgroundColor: '#f8f9ff' }}
          >
            <FaCloudUploadAlt style={{ fontSize: '3rem', color: '#0d6efd' }} />
            <h5 className="mt-3">
              {uploadedFile ? uploadedFile.name : 'Drop your TIFF file here or click to browse'}
            </h5>
            <p className="text-muted">Maximum file size: 20GB | Format: TIFF (.tif, .tiff)</p>
            
            <Input
              type="file"
              accept=".tif,.tiff"
              onChange={handleFileUpload}
              id="fileUpload"
              style={{ display: 'none' }}
            />
            <Label for="fileUpload" className="btn btn-primary mt-2">
              Select File
            </Label>

            {uploadProgress > 0 && uploadProgress < 100 && (
              <div className="mt-3">
                <Progress value={uploadProgress} animated />
                <p className="mt-2 text-primary fw-semibold">Uploading... {uploadProgress}%</p>
              </div>
            )}
          </div>

          <Alert color="warning">
            <h6 className="mb-2">📸 How to Capture & Prepare Your Orthomosaic</h6>
            <ul className="mb-0 small">
              <li>Placeholder: Add your specific instructions here for optimal data collection</li>
            </ul>
          </Alert>

          {/* Upload Consent Checkbox */}
          <Alert color="info" className="mt-3">
            <div className="form-check">
              <Input
                type="checkbox"
                id="uploadConsent"
                checked={uploadConsentGiven}
                onChange={(e) => setUploadConsentGiven(e.target.checked)}
              />
              <Label check for="uploadConsent">
                <strong>Data Usage Consent *</strong><br />
                <small>
                  I consent to RISE using my uploaded dataset for model training purposes. 
                  Your data will be stored securely for 14 days and may be used to improve 
                  plant detection accuracy.
                </small>
              </Label>
            </div>
          </Alert>
        </ModalBody>
        <ModalFooter>
          <Button 
            color="primary" 
            onClick={handleUploadSubmit} 
            disabled={!uploadedFile || !datasetName.trim() || isLoading}
          >
            {isLoading ? <Spinner size="sm" /> : 'Upload'}
          </Button>
          <Button color="secondary" onClick={toggleUploadModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {/* Configuration Modal */}
      <Modal isOpen={configModal} toggle={toggleConfigModal} size="lg">
        <ModalHeader toggle={toggleConfigModal}>Configure Processing Parameters</ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="species">Crop Species *</Label>
              <Input
                type="select"
                id="species"
                value={selectedSpecies}
                onChange={(e) => {
                  setSelectedSpecies(e.target.value);
                  setSeedingRate('');
                  setSelectedModel('');
                }}
              >
                <option value="">Select a species</option>
                {speciesOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Input>
            </FormGroup>

            {selectedSpecies && (
              <FormGroup>
                <Label for="seedingRate">Expected Seeding Rate *</Label>
                <Input
                  type="select"
                  id="seedingRate"
                  value={selectedSeedingRate}
                  onChange={(e) => setSeedingRate(e.target.value)}
                >
                  <option value="">Select seeding rate</option>
                  {seedingRateOptions[selectedSpecies]?.map((rate, idx) => (
                    <option key={idx} value={rate}>
                      {rate}
                    </option>
                  ))}
                </Input>
              </FormGroup>
            )}

            {selectedSpecies && (
              <FormGroup>
                <Label>Detection Model *</Label>
                
                <div className="form-check">
                  <Input
                    type="radio"
                    id="existingModel"
                    checked={!useCustomModel}
                    onChange={() => setUseCustomModel(false)}
                  />
                  <Label check for="existingModel">
                    Use existing model
                  </Label>
                </div>

                {!useCustomModel && (
                  <Input
                    type="select"
                    className="mt-2 ms-4"
                    value={selectedModel}
                    onChange={(e) => setSelectedModel(e.target.value)}
                  >
                    <option value="">Select a model</option>
                    {modelOptions[selectedSpecies]?.map((model, idx) => (
                      <option key={idx} value={model}>
                        {model}
                      </option>
                    ))}
                  </Input>
                )}

                <div className="form-check mt-2">
                  <Input
                    type="radio"
                    id="customModel"
                    checked={useCustomModel}
                    onChange={() => setUseCustomModel(true)}
                  />
                  <Label check for="customModel">
                    Upload custom model
                  </Label>
                </div>

                {useCustomModel && (
                  <div className="ms-4 mt-2">
                    <Input
                      type="file"
                      accept=".h5,.pt,.pth,.onnx"
                      onChange={(e) => setCustomModel(e.target.files[0])}
                    />
                    <small className="text-muted">Accepted formats: .h5, .pt, .pth, .onnx</small>
                  </div>
                )}
              </FormGroup>
            )}

            <Alert color="danger" className="mt-4">
              <div className="form-check">
                <Input
                  type="checkbox"
                  id="consent"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.target.checked)}
                />
                <Label check for="consent">
                  <strong>Data Usage Consent *</strong><br />
                  <small>
                    I consent to RISE using my uploaded data for model training purposes. 
                    Your data will be stored securely for 2 weeks and used to improve detection accuracy.
                  </small>
                </Label>
              </div>
            </Alert>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button 
            color="primary" 
            onClick={handleConfigSubmit}
            disabled={!selectedSpecies || !selectedSeedingRate || (!selectedModel && !customModel) || !consentGiven}
          >
            Run Detection Process
          </Button>
          <Button color="secondary" onClick={toggleConfigModal}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default PlantStandCount;